/**
 * Recently Viewed Products
 * Tracks and displays products the user has viewed using localStorage.
 * Works for both logged-in and guest users.
 */

class RecentlyViewedProducts {
  static STORAGE_KEY = 'recently_viewed_products';
  static MAX_PRODUCTS = 12;
  static MAX_AGE_DAYS = 90;
  static SCHEMA_VERSION = 1;

  constructor() {
    this.products = this.loadProducts();
  }

  /**
   * Check if localStorage is available (may not be in private browsing)
   */
  static isStorageAvailable() {
    try {
      const test = '__recently_viewed_test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch (e) {
      return false;
    }
  }

  /**
   * Load products from localStorage, filtering out expired ones
   */
  loadProducts() {
    if (!RecentlyViewedProducts.isStorageAvailable()) {
      return [];
    }

    try {
      const data = JSON.parse(localStorage.getItem(RecentlyViewedProducts.STORAGE_KEY));
      if (!data || data.version !== RecentlyViewedProducts.SCHEMA_VERSION) {
        return [];
      }

      // Filter out expired products (older than MAX_AGE_DAYS)
      const cutoffTime = Date.now() - (RecentlyViewedProducts.MAX_AGE_DAYS * 24 * 60 * 60 * 1000);
      return data.products.filter(p => p.viewedAt > cutoffTime);
    } catch (e) {
      console.warn('Recently viewed: Failed to load', e);
      return [];
    }
  }

  /**
   * Save products to localStorage
   */
  saveProducts() {
    if (!RecentlyViewedProducts.isStorageAvailable()) {
      return;
    }

    try {
      localStorage.setItem(RecentlyViewedProducts.STORAGE_KEY, JSON.stringify({
        version: RecentlyViewedProducts.SCHEMA_VERSION,
        products: this.products
      }));
    } catch (e) {
      // Handle quota exceeded - remove oldest products and retry
      if (e.name === 'QuotaExceededError') {
        this.products = this.products.slice(0, Math.floor(this.products.length / 2));
        try {
          localStorage.setItem(RecentlyViewedProducts.STORAGE_KEY, JSON.stringify({
            version: RecentlyViewedProducts.SCHEMA_VERSION,
            products: this.products
          }));
        } catch (e2) {
          console.warn('Recently viewed: Storage full', e2);
        }
      } else {
        console.warn('Recently viewed: Failed to save', e);
      }
    }
  }

  /**
   * Track a product view - adds to the list or moves to front if already exists
   */
  trackProduct(productData) {
    if (!productData || !productData.id) {
      return;
    }

    // Remove existing entry for this product (deduplication)
    this.products = this.products.filter(p => p.id !== productData.id);

    // Add new entry at the beginning (most recent first)
    this.products.unshift({
      ...productData,
      viewedAt: Date.now()
    });

    // Enforce max products limit
    if (this.products.length > RecentlyViewedProducts.MAX_PRODUCTS) {
      this.products = this.products.slice(0, RecentlyViewedProducts.MAX_PRODUCTS);
    }

    this.saveProducts();

    // Publish event for other components
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.recentlyViewedUpdate, { products: this.products });
    }
  }

  /**
   * Get products for display, optionally excluding a specific product
   */
  getProducts(excludeId = null, limit = 8) {
    let products = this.products;
    if (excludeId) {
      products = products.filter(p => p.id !== excludeId);
    }
    return products.slice(0, limit);
  }

  /**
   * Clear all recently viewed products
   */
  clear() {
    this.products = [];
    this.saveProducts();
  }
}

// Initialize global instance
if (typeof window !== 'undefined') {
  window.recentlyViewed = new RecentlyViewedProducts();
}

/**
 * Custom element for displaying recently viewed products
 */
class RecentlyViewedProductsElement extends HTMLElement {
  constructor() {
    super();
    this.grid = null;
    this.emptyState = null;
    this.unsubscribe = null;
  }

  connectedCallback() {
    this.grid = this.querySelector('.recently-viewed__grid');
    this.emptyState = this.querySelector('.recently-viewed__empty');
    this.sliderComponent = this.querySelector('slider-component');

    // Get configuration from data attributes
    this.config = {
      enabled: this.dataset.enabled !== 'false',
      productsToShow: parseInt(this.dataset.productsToShow) || 8,
      columnsDesktop: parseInt(this.dataset.columnsDesktop) || 4,
      columnsMobile: this.dataset.columnsMobile || '2',
      currentProductId: this.dataset.currentProductId ? parseInt(this.dataset.currentProductId) : null,
      showSecondaryImage: this.dataset.showSecondaryImage === 'true',
      showVendor: this.dataset.showVendor === 'true',
      imageRatio: this.dataset.imageRatio || 'square',
      swipeOnMobile: this.dataset.swipeOnMobile === 'true',
      enableDesktopSlider: this.dataset.enableDesktopSlider === 'true',
      sectionId: this.dataset.sectionId
    };

    // Don't render if disabled
    if (!this.config.enabled) {
      this.remove(); // Remove from DOM entirely if disabled
      return;
    }

    this.render();

    // Subscribe to updates
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.recentlyViewedUpdate, () => {
        this.render();
      });
    }
  }

  disconnectedCallback() {
    if (this.unsubscribe) {
      this.unsubscribe();
    }
  }

  render() {
    if (!window.recentlyViewed || !RecentlyViewedProducts.isStorageAvailable()) {
      this.remove(); // Remove from DOM if storage unavailable
      return;
    }

    const products = window.recentlyViewed.getProducts(
      this.config.currentProductId,
      this.config.productsToShow
    );

    if (products.length === 0) {
      this.remove(); // Remove from DOM if no products
      return;
    }

    // Show the section with smooth transition
    this.classList.add('is-loaded');

    // Determine if sliders should be enabled
    const columnsMobileInt = parseInt(this.config.columnsMobile);
    const columnsDesktopInt = this.config.columnsDesktop;
    const showMobileSlider = this.config.swipeOnMobile && products.length > columnsMobileInt;
    const showDesktopSlider = this.config.enableDesktopSlider && products.length > columnsDesktopInt;
    const showAnySlider = showMobileSlider || showDesktopSlider;

    // Update grid classes for slider
    if (this.grid) {
      // Remove all slider classes first
      this.grid.classList.remove('slider', 'slider--tablet', 'slider--desktop', 'grid--peek');

      if (showDesktopSlider && showMobileSlider) {
        // Both mobile and desktop slider
        this.grid.classList.add('slider', 'slider--tablet', 'slider--desktop', 'grid--peek');
      } else if (showMobileSlider) {
        // Mobile slider only (tablet and below)
        this.grid.classList.add('slider', 'slider--tablet', 'grid--peek');
      } else if (showDesktopSlider) {
        // Desktop slider only (990px+)
        this.grid.classList.add('slider', 'slider--desktop', 'grid--peek');
      }

      // Render product cards
      this.grid.innerHTML = products.map((product, index) =>
        this.renderProductCard(product, index === 0, showAnySlider)
      ).join('');
    }

    // Re-initialize slider component if needed
    if (this.sliderComponent && typeof this.sliderComponent.resetPages === 'function') {
      this.sliderComponent.resetPages();
    }
  }

  renderProductCard(product, isFirst, showSlider) {
    const ratio = this.getImageRatio(product);
    const slideClass = showSlider ? ' slider__slide' : '';
    const sectionId = this.config.sectionId;
    const hasImage = product.featuredImage && product.featuredImage.url;

    return `
      <li class="grid__item${slideClass}">
        <div class="card-wrapper product-card-wrapper underline-links-hover">
          <div class="card card--standard${hasImage ? ' card--media' : ''}" style="--ratio-percent: ${ratio}%;">
            <div class="card__inner color-scheme-1 gradient ratio" style="--ratio-percent: ${ratio}%;">
              ${this.renderCardMedia(product)}
              <div class="card__content">
                <div class="card__information">
                  <h3 class="card__heading">
                    <a href="${this.escapeHtml(product.url)}" class="full-unstyled-link">
                      ${this.escapeHtml(product.title)}
                    </a>
                  </h3>
                </div>
                ${this.renderBadge(product, sectionId)}
              </div>
            </div>
            <div class="card__content">
              <div class="card__information">
                <h3 class="card__heading h5">
                  <a href="${this.escapeHtml(product.url)}" id="CardLink-${sectionId}-${product.id}" class="full-unstyled-link">
                    ${this.escapeHtml(product.title)}
                  </a>
                </h3>
                <div class="card-information">
                  ${this.config.showVendor && product.vendor ? `
                    <span class="visually-hidden">Vendor</span>
                    <div class="caption-with-letter-spacing light">${this.escapeHtml(product.vendor)}</div>
                  ` : ''}
                  ${this.renderPrice(product)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </li>
    `;
  }

  renderCardMedia(product) {
    const hasImage = product.featuredImage && product.featuredImage.url;

    if (!hasImage) {
      // Return placeholder SVG for products without images
      return `
        <div class="card__media">
          <div class="media media--transparent">
            <svg class="placeholder-svg" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 525.5 525.5">
              <path d="M324.5 212.7H203c-1.6 0-2.8 1.3-2.8 2.8V308c0 1.6 1.3 2.8 2.8 2.8h121.6c1.6 0 2.8-1.3 2.8-2.8v-92.5c0-1.6-1.3-2.8-2.9-2.8zm1.1 95.3c0 .6-.5 1.1-1.1 1.1H203c-.6 0-1.1-.5-1.1-1.1v-92.5c0-.6.5-1.1 1.1-1.1h121.6c.6 0 1.1.5 1.1 1.1V308z" fill="currentColor"/>
              <path d="M210.4 299.5H240v.1s.1 0 .2-.1h75.2v-76.2h-105v76.2zm1.8-7.2l20-20c1.6-1.6 3.8-2.5 6.1-2.5s4.5.9 6.1 2.5l1.5 1.5 16.8 16.8c-12.9 3.3-20.7 6.3-22.8 7.2h-27.7v-5.5zm101.5-67.2v72.2c-7.2-1.9-15.3-3-24.3-3-22.7 0-39.1 6.4-48.1 11l-18.2-18.2 6.3-6.3c1.6-1.6 3.8-2.5 6.1-2.5s4.5.9 6.1 2.5l6.3 6.3 43.1-43.1c2.3-2.3 6.2-2.3 8.5 0l14.2 14.3v-33.2zm-68.9 42.4c0 4.6-3.7 8.4-8.4 8.4-4.6 0-8.4-3.7-8.4-8.4 0-4.6 3.7-8.4 8.4-8.4 4.7 0 8.4 3.8 8.4 8.4z" fill="currentColor"/>
            </svg>
          </div>
        </div>
      `;
    }

    const secondaryImage = this.config.showSecondaryImage && product.secondaryImage
      ? `<img
          src="${this.escapeHtml(product.secondaryImage.url)}"
          alt="${this.escapeHtml(product.secondaryImage.alt || '')}"
          class="motion-reduce"
          loading="lazy"
          width="${product.secondaryImage.width}"
          height="${product.secondaryImage.height}"
        >`
      : '';

    return `
      <div class="card__media">
        <div class="media media--transparent media--hover-effect">
          <img
            src="${this.escapeHtml(product.featuredImage.url)}"
            alt="${this.escapeHtml(product.featuredImage.alt || product.title)}"
            class="motion-reduce"
            loading="lazy"
            width="${product.featuredImage.width}"
            height="${product.featuredImage.height}"
          >
          ${secondaryImage}
        </div>
      </div>
    `;
  }

  renderBadge(product, sectionId) {
    // TODO: Re-enable sold out badge when inventory tracking is ready
    // if (!product.available) {
    //   return `
    //     <div class="card__badge bottom left">
    //       <span id="Badge-${sectionId}-${product.id}" class="badge badge--bottom-left color-scheme-4">Sold out</span>
    //     </div>
    //   `;
    // }
    if (product.compareAtPrice && product.compareAtPrice > product.price && product.available) {
      return `
        <div class="card__badge bottom left">
          <span id="Badge-${sectionId}-${product.id}" class="badge badge--bottom-left color-scheme-4">Sale</span>
        </div>
      `;
    }
    return '<div class="card__badge bottom left"></div>';
  }

  renderPrice(product) {
    // Apply 8% GST as per the theme's price.liquid pattern
    const GST_MULTIPLIER = 1.08;

    const formatMoney = (cents) => {
      const priceWithGst = cents * GST_MULTIPLIER;
      // Format as MVR (Maldivian Rufiyaa)
      const formatted = (priceWithGst / 100).toFixed(2);
      return `MVR ${formatted}`;
    };

    const isOnSale = product.compareAtPrice && product.compareAtPrice > product.price && product.available;
    const isSoldOut = !product.available;

    let priceClasses = 'price';
    if (isSoldOut) priceClasses += ' price--sold-out';
    if (isOnSale) priceClasses += ' price--on-sale';

    let priceHtml = `<div class="${priceClasses}">`;
    priceHtml += '<div class="price__container">';

    if (isOnSale) {
      priceHtml += `
        <div class="price__sale">
          <span class="visually-hidden">Regular price</span>
          <span><s class="price-item price-item--regular">${formatMoney(product.compareAtPrice)}</s></span>
          <span class="visually-hidden">Sale price</span>
          <span class="price-item price-item--sale price-item--last">${formatMoney(product.price)}</span>
        </div>
      `;
    } else {
      priceHtml += `
        <div class="price__regular">
          <span class="visually-hidden">Regular price</span>
          <span class="price-item price-item--regular">${formatMoney(product.price)}</span>
        </div>
      `;
    }

    priceHtml += '</div></div>';
    return priceHtml;
  }

  getImageRatio(product) {
    if (!product.featuredImage) return 100;

    switch (this.config.imageRatio) {
      case 'portrait':
        return 125; // 0.8 ratio = 1/0.8 * 100 = 125%
      case 'square':
        return 100;
      case 'adapt':
      default:
        if (product.featuredImage.width && product.featuredImage.height) {
          const ratio = product.featuredImage.height / product.featuredImage.width;
          return Math.round(ratio * 100);
        }
        return 100;
    }
  }

  escapeHtml(text) {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

// Define custom element
if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedProductsElement);
}
